import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import type { Head} from '../../types';

interface HeadWiseDonutChartProps {
  data: Head[];
  height?: number;
  showLegend?: boolean;
  centerLabel?: string;
  centerValue?: string;
  valueType?: 'sanctioned' | 'received' | 'spent' | 'utilization';
}

const HeadWiseDonutChart: React.FC<HeadWiseDonutChartProps> = ({
  data,
  height = 300,
  showLegend = true,
  centerLabel = 'Total',
  centerValue,
  valueType = 'sanctioned',
}) => {
  const chartData = data.map((head) => ({
    name: head.name,
    value: valueType === 'sanctioned' 
      ? head.totalSanctioned 
      : valueType === 'received'
        ? head.totalReceived
        : valueType === 'spent' 
          ? head.totalSpent 
          : head.utilizationPercentage,
    color: head.color,
    code: head.code,
    utilization: head.utilizationPercentage,
    sanctioned: head.totalSanctioned,
    received: head.totalReceived,
    spent: head.totalSpent,
  }));

  const total = chartData.reduce((acc, item) => acc + item.value, 0);
  const displayValue = centerValue || (valueType === 'utilization' 
    ? `${(total / data.length).toFixed(1)}%`
    : `₹${total.toLocaleString('en-IN', { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      
      const formatAmount = (value: number) => {
        if (value >= 10000000) {
          return `₹${value / 10000000} Cr`;
        } else if (value >= 100000) {
          return `₹${value / 100000} L`;
        }
        return `₹${value}`;
      };

      const getModeLabel = () => {
        switch(valueType) {
          case 'sanctioned':
            return `Sanctioned: ${formatAmount(item.sanctioned)}`;
          case 'received':
            return `Received: ${formatAmount(item.received)}`;
          case 'spent':
            return `Spent: ${formatAmount(item.spent)}`;
          case 'utilization':
            return `Utilization: ${item.value}%`;
          default:
            return `Amount: ${formatAmount(item.value)}`;
        }
      };

      return (
        <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
          <p className="font-semibold text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-600">
            {getModeLabel()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry: any) => (
                <span className="text-xs text-gray-700">{value}</span>
              )}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
      
      {/* Center Label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
        <span className="text-xs sm:text-sm text-gray-500">{centerLabel}</span>
        <span className="text-sm sm:text-base md:text-lg font-bold text-gray-900 px-2 text-center break-all">{displayValue}</span>
      </div>
    </div>
  );
};

export default HeadWiseDonutChart;
